import React, { useCallback, useMemo } from 'react';
import { useAppSelector, useAuthSelector, useUserSelector } from 'js/store/hooks';
import { useParams } from 'react-router-dom';
import { path, pick, prop } from 'ramda';
import { Redirect } from 'js/components/common/Redirect';
import { Loader } from 'js/components/common/Loader';
import { WebContent } from 'js/components/system/WebContent';
import { Button } from 'js/components/common/Button';
import { EventTrigger } from 'js/components/common/EventTrigger';
import { useTranslations } from 'js/hooks/i18n';
import { getCleanPath } from 'js/utils/url.util';
import { useNavigate } from 'js/hooks/navigate';

import * as constants from './constants';
import * as hooks from './hooks';

import style from './Promotion.scss';

export const Promotion: React.FC<{ id?: string; allowRedirects?: boolean }> = ({ id, allowRedirects = false }) => {
    const translate = useTranslations();
    const { seoFriendlyBonusName = '' } = useParams<{ seoFriendlyBonusName: string }>();
    const bonusPage = getCleanPath(useAppSelector(
        path(['bonusManagementPreferences', 'bonusDeepLinkingUrl'])
    ) || 'promotions');
    const isLoggedIn = useUserSelector(prop('isLoggedIn'));
    const bonusId = id?.toLowerCase() ?? seoFriendlyBonusName;
    const { rootPage, bonusesConfiguration = [] } = useAppSelector(pick(['rootPage', 'bonusesConfiguration']));
    const bonus = bonusesConfiguration.find(item => item.seoFriendlyBonusName === bonusId ?? '');
    const logicButton = hooks.useLogicButton({ bonus: bonus! });
    const {
        isLoginInProcess,
        isLogoutInProcess
    } = useAuthSelector(pick(['isLoginInProcess', 'isLogoutInProcess']));
    const navigate = useNavigate();
    const onClick = useCallback(() => {
        if (!bonus || allowRedirects) {
            return;
        }

        navigate(`${bonusPage}/${bonus.seoFriendlyBonusName ?? bonus.bonusCode}`);
    }, [
        bonus,
        navigate,
        bonusPage,
        allowRedirects
    ]);

    const actionTitle = useMemo(() => {
        if (!bonus) {
            return '';
        }

        const { bonusTrigger, bonusCode } = bonus;

        if (bonusTrigger === 'CUSTOM' || bonusTrigger === 'CAMPAIGN') {
            const bonusType = bonusTrigger === 'CUSTOM' ? 'custombonus' : 'campaignbonus';

            return isLoggedIn
                ? `playtech.mobile.system.bonus.${bonusCode}.${bonusType}.logged-in`
                : `playtech.mobile.system.bonus.${bonusCode}.${bonusType}.logged-out`;
        }

        return constants.DEFAULT_ACTION_TITLES[bonusTrigger as keyof typeof constants.DEFAULT_ACTION_TITLES] ?? '';

    }, [
        bonus,
        isLoggedIn
    ]);

    if (!bonus) {
        if (allowRedirects) {
            return <Redirect to={rootPage} />;
        }

        return null;
    }

    const {
        showFor,
        listViewWebContentId,
        detailedViewWebContentId,
        termAndConditionWebContentId
    } = bonus;

    const isShowForLoggedIn = showFor === 'LOGGED_IN';
    const isShowForBoth = showFor === 'BOTH';

    if (!isShowForBoth && isLoggedIn !== isShowForLoggedIn) {
        if (!isLoggedIn) {
            // show login popup maybe?
        }

        if (allowRedirects) {
            return <Redirect to={rootPage} />;
        }
        return null;
    }

    if (isLoginInProcess || isLogoutInProcess) {
        return <Loader className={style.loader} />;
    }

    return <div data-bonus-code={bonus.bonusCode} className={style.promotion}>
        <EventTrigger eventName="promotion-shown" eventArgs={{ bonus }} />
        <div className={style.details} onClick={onClick}>
            <WebContent id={allowRedirects ? detailedViewWebContentId : listViewWebContentId} />
        </div>
        {termAndConditionWebContentId && (
            <div className={style.terms} onClick={onClick}>
                <WebContent id={termAndConditionWebContentId} />
            </div>
        )}
        <div className={style.button}>
            <Button isNew={true} onClick={logicButton}>{translate(actionTitle)}</Button>
        </div>
    </div>;
};

export default Promotion;
